import { useState, useEffect } from 'react';

type RatingFormProps = {
  onRate: (rating: number) => void;
  onSkip: () => void;
  existingRating?: number;
  isUpdate: boolean;
  characterName?: string;
  animeName?: string;
  isCharacterPage?: boolean; // Add prop to identify character page usage
};

export default function RatingForm({ onRate, onSkip, existingRating, isUpdate, characterName, animeName, isCharacterPage = false }: RatingFormProps) {
  const [selectedRating, setSelectedRating] = useState<number | null>(existingRating || null);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [justRated, setJustRated] = useState(false);

  // Update selectedRating when existingRating changes (new character loaded)
  useEffect(() => {
    setSelectedRating(existingRating || null);
    setShowShareOptions(false);
    setJustRated(false);
  }, [existingRating]);

  const handleSubmit = () => {
    if (selectedRating) {
      onRate(selectedRating);
      setJustRated(true);
      setShowShareOptions(true);
    }
  };

  const handleShare = async (platform: string) => {
    const rating = selectedRating || existingRating;
    if (!rating) return;

    const text = characterName && animeName
      ? `I just rated ${characterName} from ${animeName} a ${rating}/10! 🔥`
      : `I just rated this character a ${rating}/10! 🔥`;
    
    const url = window.location.href;

    // Handle native sharing (works great on mobile)
    if (platform === 'native') {
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'My Rating',
            text: text,
            url: url,
          });
          return;
        } catch (err) {
          console.log('Share cancelled or failed');
          return;
        }
      }
    }

    let shareUrl = '';
    
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
        break;
      case 'reddit':
        shareUrl = `https://reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(text)}`;
        break;
      case 'telegram':
        shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
        break;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  const displayRating = hoverRating || selectedRating;

  // Determine heart size based on context and screen size
  const getHeartSizeClasses = () => {
    if (isCharacterPage) {
      // Smaller hearts for character page desktop, but keep mobile sizes
      return "text-2xl sm:text-3xl md:text-4xl lg:text-5xl";
    } else {
      // Larger hearts for rating page (unchanged)
      return "text-3xl sm:text-4xl md:text-6xl lg:text-7xl";
    }
  };

  const getNumberSizeClasses = () => {
    if (isCharacterPage) {
      // Smaller numbers for character page desktop
      return "text-xs sm:text-sm md:text-base";
    } else {
      // Larger numbers for rating page (unchanged) 
      return "text-sm sm:text-base md:text-lg";
    }
  };

  return (
    <div>
      {isUpdate && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            You've already rated this character. You can update your rating below.
          </p>
        </div>
      )}
      
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <span className="text-sm font-medium text-gray-700">NOT</span>
          <span className="text-sm font-medium text-gray-700">HOT</span>
        </div>
        
        <div className="grid grid-cols-10 gap-1 sm:gap-2 md:gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
            <label
              key={rating}
              className="relative cursor-pointer"
              onMouseEnter={() => setHoverRating(rating)}
              onMouseLeave={() => setHoverRating(null)}
            >
              <input
                type="radio"
                name="rating"
                value={rating}
                checked={selectedRating === rating}
                onChange={() => setSelectedRating(rating)}
                className="sr-only"
              />
              <div className="aspect-square flex items-center justify-center relative">
                <i 
                  className={`ri-heart-${displayRating && rating <= displayRating ? 'fill' : 'line'} ${getHeartSizeClasses()} transition-all ${
                    displayRating && rating <= displayRating
                      ? 'text-pink-500 scale-110'
                      : 'text-gray-300'
                  }`}
                ></i>
                <span className={`absolute inset-0 flex items-center justify-center ${getNumberSizeClasses()} font-bold transition-all ${
                  displayRating && rating <= displayRating
                    ? 'text-white'
                    : 'text-gray-500'
                }`}>
                  {rating}
                </span>
              </div>
            </label>
          ))}
        </div>
        
        {displayRating && (
          <div className="text-center mt-4">
            <span className="text-lg font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
              {displayRating} / 10
            </span>
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleSubmit}
          disabled={!selectedRating}
          className={`flex-1 py-3 text-base font-semibold rounded-full transition-all whitespace-nowrap ${
            selectedRating
              ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:shadow-lg cursor-pointer'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isUpdate ? 'Update Rating' : 'Submit Rating'}
        </button>
        <button
          onClick={onSkip}
          className="px-8 py-3 text-base font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-full hover:border-gray-400 hover:shadow-md transition-all whitespace-nowrap cursor-pointer"
        >
          Skip
        </button>
      </div>

      {(justRated || (existingRating && !justRated)) && (
        <div className="mt-4">
          <button
            onClick={() => setShowShareOptions(!showShareOptions)}
            className="w-full py-3 text-sm font-semibold text-pink-600 bg-pink-50 border-2 border-pink-200 rounded-full hover:bg-pink-100 transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
          >
            <i className="ri-share-line text-lg"></i>
            Share Your Rating
          </button>

          {showShareOptions && (
            <div className="mt-3 p-4 bg-white border-2 border-gray-200 rounded-xl">
              <p className="text-xs font-medium text-gray-600 mb-3 text-center">Share on:</p>
              <div className="grid grid-cols-5 gap-2">
                <button
                  onClick={() => handleShare('twitter')}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-gray-50 transition-all cursor-pointer group"
                  title="Share on Twitter"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-black rounded-full group-hover:scale-110 transition-transform">
                    <i className="ri-twitter-x-fill text-xl text-white"></i>
                  </div>
                  <span className="text-xs text-gray-600">Twitter</span>
                </button>

                <button
                  onClick={() => handleShare('facebook')}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-gray-50 transition-all cursor-pointer group"
                  title="Share on Facebook"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1877F2] rounded-full group-hover:scale-110 transition-transform">
                    <i className="ri-facebook-fill text-xl text-white"></i>
                  </div>
                  <span className="text-xs text-gray-600">Facebook</span>
                </button>

                <button
                  onClick={() => handleShare('whatsapp')}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-gray-50 transition-all cursor-pointer group"
                  title="Share on WhatsApp"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#25D366] rounded-full group-hover:scale-110 transition-transform">
                    <i className="ri-whatsapp-fill text-xl text-white"></i>
                  </div>
                  <span className="text-xs text-gray-600">WhatsApp</span>
                </button>

                <button
                  onClick={() => handleShare('reddit')}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-gray-50 transition-all cursor-pointer group"
                  title="Share on Reddit"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#FF4500] rounded-full group-hover:scale-110 transition-transform">
                    <i className="ri-reddit-fill text-xl text-white"></i>
                  </div>
                  <span className="text-xs text-gray-600">Reddit</span>
                </button>

                <button
                  onClick={() => handleShare('telegram')}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-gray-50 transition-all cursor-pointer group"
                  title="Share on Telegram"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0088cc] rounded-full group-hover:scale-110 transition-transform">
                    <i className="ri-telegram-fill text-xl text-white"></i>
                  </div>
                  <span className="text-xs text-gray-600">Telegram</span>
                </button>
              </div>
              
              {navigator.share && (
                <>
                  <div className="my-3 border-t border-gray-200"></div>
                  <button
                    onClick={() => handleShare('native')}
                    className="w-full py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all cursor-pointer flex items-center justify-center gap-2"
                  >
                    <i className="ri-share-line"></i>
                    More Options
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
